// styles/tagUtils.js - Système de couleurs unifié pour les tags

// Liste des villes pour lesquelles appliquer un dégradé
export const citiesWithGradient = [
  "paris", "marseille", "lyon", "toulouse", "bordeaux", "nice", "toulon", 
  "avignon", "rennes", "strasbourg", "montpellier", "metz", "aix-en-provence", 
  "monaco", "dijon", "grenoble", "nancy"
];

// Palette de couleurs prédéfinies pour les types de tags
export const colorPalette = [
  '#3B82F6', // Blue
  '#10B981', // Emerald  
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F472B6', // Pink
  '#A855F7', // Purple
  '#22C55E', // Green
  '#EAB308', // Yellow
];

// Fonction pour obtenir une couleur basée sur le nom du type (hash consistant)
export const getColorForType = (typeName) => {
  if (!typeName) return '#6366F1'; // Couleur par défaut
  
  // Générer un index basé sur le nom du type pour avoir une couleur consistante
  let hash = 0;
  for (let i = 0; i < typeName.length; i++) {
    hash = typeName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colorPalette.length;
  return colorPalette[index];
};

// Fonction pour obtenir les styles CSS d'un tag selon ses types (pour ViewAllTags)
export const getTagColor = (tag) => {
  if (tag.type && tag.type.length > 0) {
    // Utiliser la couleur du premier type
    const firstType = tag.type.find(type => type && type.name);
    if (firstType) {
      const color = '#3B82F6';
      return {
        backgroundColor: color + '20', // 20 = transparence 12%
        borderColor: color,
        color: color
      };
    }
  }
  // Couleur par défaut pour les tags sans type
  const defaultColor = '#3B82F6';
  return {
    backgroundColor: defaultColor + '20',
    borderColor: defaultColor, 
    color: defaultColor
  };
};

// Fonction pour obtenir la classe CSS en fonction du tag (pour l'affichage simple avec Tailwind)
// styles/tagUtils.js
export const getTagStyle = (tag) => {
  if (!tag) return 'bg-gray-200 text-gray-800';
  
  // Si c'est juste une string
  if (typeof tag === 'string') {
    const tagName = tag.toLowerCase();
    if (tagName === "man") return 'bg-black text-white';
    if (citiesWithGradient.includes(tagName)) return 'bg-cyan-700 text-white';
    return 'bg-orange-200 text-slate-800';
  }
  
  // Si c'est un objet tag
  const tagName = (tag.name || '').toLowerCase();
  
  // Styles spéciaux basés sur le nom
  if (tagName === "man") {
    return 'bg-pink-100 text-pink-800 border-pink-300';
  }
  
  if (citiesWithGradient.includes(tagName)) {
    return 'bg-purple-100 text-purple-800 border-purple-300';
  }
  
  // Si le tag a des types populés
  if (tag.type && Array.isArray(tag.type) && tag.type.length > 0) {
    const firstType = tag.type[0];
    
    // Vérifier si le type est populé (a une propriété name)
    if (firstType && firstType.name) {
      const color = '#6366F1';
      return getTagClassFromColor(color);
    }
  }
  
  // Style par défaut
  return 'bg-indigo-100 text-indigo-800';
};

// Fonction utilitaire pour convertir une couleur hex en classes Tailwind
export const getTagClassFromColor = (hexColor) => {
  const colorMap = {
    '#3B82F6': 'bg-blue-100 text-blue-800 border-blue-300',
    '#10B981': 'bg-emerald-100 text-emerald-800 border-emerald-300',
    '#F59E0B': 'bg-amber-100 text-amber-800 border-amber-300',
    '#EF4444': 'bg-red-100 text-red-800 border-red-300',
    '#8B5CF6': 'bg-violet-100 text-violet-800 border-violet-300',
    '#06B6D4': 'bg-cyan-100 text-cyan-800 border-cyan-300',
    '#84CC16': 'bg-lime-100 text-lime-800 border-lime-300',
    '#F97316': 'bg-orange-100 text-orange-800 border-orange-300',
    '#EC4899': 'bg-pink-100 text-pink-800 border-pink-300',
    '#6366F1': 'bg-indigo-100 text-indigo-800 border-indigo-300',
    '#14B8A6': 'bg-teal-100 text-teal-800 border-teal-300',
    '#F472B6': 'bg-pink-100 text-pink-800 border-pink-300',
    '#A855F7': 'bg-purple-100 text-purple-800 border-purple-300',
    '#22C55E': 'bg-green-100 text-green-800 border-green-300',
    '#EAB308': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  };

  return colorMap[hexColor] || 'bg-gray-100 text-gray-800 border-gray-300';
};

// Fonction pour obtenir un style inline cohérent pour un tag
export const getTagInlineStyle = (tag) => {
  const lowerTag = typeof tag === 'string' ? tag.toLowerCase() : (tag.name || '').toLowerCase();

  // Cas spéciaux avec gradients
  if (lowerTag === "man") {
    return {
      background: 'black',
      color: 'white',
      border: '1px solid #ec4899'
    };
  }

  if (citiesWithGradient.includes(lowerTag)) {
    return {
      background: 'bg-cyan-700',
      color: 'white',
      border: '1px solid #8b5cf6'
    };
  }

  // Pour les tags avec types
  if (tag.type && tag.type.length > 0) {
    const firstType = tag.type.find(type => type && type.name);
    if (firstType) {
      const color = getColorForType(firstType.name);
      return {
        backgroundColor: color + '20',
        color: color,
        border: `1px solid ${color}`
      };
    }
  }

  // Style par défaut
  return {
    backgroundColor: '#fed7aa', // orange-200
    color: '#1e293b', // slate-800
    border: '1px solid #fdba74' // orange-300
  };
};

// Fonction pour grouper les tags par type avec leurs couleurs
export const groupTagsByType = (tags) => {
  return tags.reduce((groups, tag) => {
    if (tag.type && tag.type.length > 0) {
      tag.type.forEach(type => {
        if (type && type.name) {
          if (!groups[type.name]) {
            groups[type.name] = {
              tags: [],
              color: getColorForType(type.name)
            };
          }
          groups[type.name].tags.push(tag);
        }
      });
    } else {
      if (!groups['Sans type']) {
        groups['Sans type'] = {
          tags: [],
          color: '#6366F1' // Couleur par défaut
        };
      }
      groups['Sans type'].tags.push(tag);
    }
    return groups;
  }, {});
};

// Export par défaut avec toutes les fonctions utiles
export default {
  citiesWithGradient,
  colorPalette,
  getColorForType,
  getTagColor,
  getTagStyle,
  getTagClassFromColor,
  getTagInlineStyle,
  groupTagsByType
};